import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import EditBookingModal from './EditBookingModal'; // Import the modal
import './ManageBookings.css'; 

const ManageBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [archivingBookingId, setArchivingBookingId] = useState(null);
  const [viewMode, setViewMode] = useState('active');
  const [archivedBookings, setArchivedBookings] = useState([]);
  const [archivePagination, setArchivePagination] = useState({});
  const [archivePage, setArchivePage] = useState(1);
  const [restoringArchiveId, setRestoringArchiveId] = useState(null);

  // State to manage the modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Function to fetch bookings for a specific page
  const fetchAllBookings = async (page) => {
    setIsLoading(true);
    // Don't clear old errors immediately, in case saving fails
    // setError(null); 
    try {
      const response = await apiService.getAllBookings({ page, limit: 10 });
      if (response.success) {
        setBookings(response.data);
        setPagination(response.pagination);
        return response;
      } else {
        throw new Error(response.message || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArchivedBookings = async (page) => {
    setIsLoading(true);
    try {
      const response = await apiService.getArchivedBookings({ page, limit: 10 });
      if (response.success) {
        setArchivedBookings(response.data);
        setArchivePagination(response.pagination);
        return response;
      } else {
        throw new Error(response.message || 'Failed to fetch archived bookings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // console.log("HEhe", bookings)
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Fetch bookings when the component mounts or when the page changes
  useEffect(() => {
    if (viewMode === 'active') {
      fetchAllBookings(currentPage);
    } else {
      fetchArchivedBookings(archivePage);
    }
  }, [currentPage, archivePage, viewMode]);

  // Handler functions for the modal
  const handleOpenModal = (booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  };

  const handleSaveBooking = async (bookingId, updateData) => {
    setError(null); // Clear previous errors before trying to save
    try {
      const response = await apiService.updateBookingStatus(bookingId, updateData);
      if (response.success) {
        // Refresh the data on the current page to show the changes
        await fetchAllBookings(currentPage);
        handleCloseModal();
        // Here you can add a success toast notification, e.g., toast.success('Booking updated!');
      } else {
        throw new Error(response.message || 'Failed to save changes');
      }
    } catch (err) {
      // If saving fails, we can display the error
      // The modal will remain open for the user to try again
      alert(`Error: ${err.message}`); // Or set an error state within the modal
    }
  };

  const handleArchiveBooking = async (booking) => {
    const confirmed = window.confirm(
      `Archive booking ${booking.bookingId}? It will be removed from active bookings and kept in archive records.`
    );

    if (!confirmed) return;

    setError(null);
    setArchivingBookingId(booking.bookingId);

    try {
      const response = await apiService.archiveBooking(booking.bookingId, {
        reason: 'Archived from Manage Bookings'
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to archive booking');
      }

      if (bookings.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchAllBookings(currentPage);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setArchivingBookingId(null);
    }
  };

  const handleRestoreBooking = async (archiveRecord) => {
    const confirmed = window.confirm(
      `Restore booking ${archiveRecord.bookingId}? It will return to active Manage Bookings.`
    );

    if (!confirmed) return;

    setError(null);
    setRestoringArchiveId(archiveRecord.archiveId);

    try {
      const response = await apiService.restoreArchivedBooking(archiveRecord.archiveId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to restore booking');
      }

      if (archivedBookings.length === 1 && archivePage > 1) {
        setArchivePage(archivePage - 1);
      } else {
        await fetchArchivedBookings(archivePage);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setRestoringArchiveId(null);
    }
  };

  // Handler for pagination
  const handlePageChange = (newPage) => {
    if (viewMode === 'archived') {
      if (newPage > 0 && newPage <= archivePagination.totalPages) {
        setArchivePage(newPage);
      }
      return;
    }

    if (newPage > 0 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const visiblePagination = viewMode === 'archived' ? archivePagination : pagination;
  const totalCount = viewMode === 'archived'
    ? archivePagination.totalArchivedBookings || 0
    : pagination.totalBookings || 0;

  if (isLoading) {
    return <div className="loading-spinner">Loading all bookings...</div>;
  }

  // Display a prominent error message if fetching fails
  if (error && viewMode === 'active' && bookings.length === 0) {
    return <div className="error-message">Error: {error}</div>;
  }

  if (error && viewMode === 'archived' && archivedBookings.length === 0) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="manage-bookings-container">
      <div className="header-section">
        <div>
          <h2>Manage Pooja Bookings</h2>
          <p>Total {viewMode === 'archived' ? 'Archived Bookings' : 'Bookings'}: {totalCount}</p>
        </div>
        <div className="view-toggle" role="group" aria-label="Booking view">
          <button
            type="button"
            className={viewMode === 'active' ? 'active' : ''}
            onClick={() => setViewMode('active')}
          >
            Active
          </button>
          <button
            type="button"
            className={viewMode === 'archived' ? 'active' : ''}
            onClick={() => setViewMode('archived')}
          >
            Archived
          </button>
        </div>
      </div>

      <div className="bookings-table-wrapper">
        <table className="bookings-table">
          <thead>
            {viewMode === 'active' ? (
              <tr>
                <th>Booking ID</th>
                <th>Devotee</th>
                <th>Pooja Type</th>
                <th>Date & Time</th>
                <th>Special Request</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            ) : (
              <tr>
                <th>Booking ID</th>
                <th>Devotee</th>
                <th>Pooja Type</th>
                <th>Date & Time</th>
                <th>Archived</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            )}
          </thead>
          <tbody>
            {viewMode === 'active' && bookings.length > 0 ? (
              bookings.map((booking) => (
                <tr key={booking.bookingId}>
                  <td data-label="Booking ID">{booking.bookingId}</td>
                  <td data-label="Devotee">
                    {booking.devoteeName}<br />
                    {booking.phone}<br />
                    {booking.gotra && <small className="text-accent">Gotra: {booking.gotra}<br /></small>}
                    <small>{booking.email}</small>
                  </td>
                  <td data-label="Pooja Details">
                    {booking.poojaType} <br />
                    <small>{booking.temple}</small> <br />
                  </td>
                  <td data-label="Date & Time">
                    {booking.poojaDate}<br />
                    <small>{booking.poojaTime}</small>
                  </td>
                  <td data-label="Special Request" className="special-request-cell">
                    <div className="scrollable-text">
                      {booking.specialRequests || 'None'}
                    </div>
                  </td>
                  <td data-label="Payment">
                    <span className={`status-badge status-${booking.paymentStatus}`}>
                      {booking.paymentStatus}
                    </span>
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge status-${booking.status || 'pending'}`}>
                      {booking.status || 'pending'}
                    </span>
                  </td>
                  <td data-label="Actions" className="actions-cell">
                    <div className="booking-actions">
                      <button
                        className="action-btn"
                        onClick={() => handleOpenModal(booking)}
                        disabled={archivingBookingId === booking.bookingId}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button
                        className="action-btn archive-btn"
                        onClick={() => handleArchiveBooking(booking)}
                        disabled={archivingBookingId === booking.bookingId}
                      >
                        {archivingBookingId === booking.bookingId ? 'Archiving...' : 'Archive'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : viewMode === 'archived' && archivedBookings.length > 0 ? (
              archivedBookings.map((archiveRecord) => (
                <tr key={archiveRecord.archiveId}>
                  <td data-label="Booking ID">{archiveRecord.bookingId}</td>
                  <td data-label="Devotee">
                    {archiveRecord.original.devoteeName}<br />
                    {archiveRecord.original.phone}<br />
                    {archiveRecord.original.gotra && <small className="text-accent">Gotra: {archiveRecord.original.gotra}<br /></small>}
                    <small>{archiveRecord.original.email}</small>
                  </td>
                  <td data-label="Pooja Details">
                    {archiveRecord.original.poojaType} <br />
                    <small>{archiveRecord.original.temple}</small> <br />
                  </td>
                  <td data-label="Date & Time">
                    {archiveRecord.original.poojaDate}<br />
                    <small>{archiveRecord.original.poojaTime}</small>
                  </td>
                  <td data-label="Archived">
                    {new Date(archiveRecord.archivedAt).toLocaleDateString()}<br />
                    <small>{archiveRecord.archivedBy?.fullName || archiveRecord.archivedBy?.email || 'Admin'}</small>
                  </td>
                  <td data-label="Payment">
                    <span className={`status-badge status-${archiveRecord.original.paymentStatus}`}>
                      {archiveRecord.original.paymentStatus}
                    </span>
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge status-${archiveRecord.original.status || 'pending'}`}>
                      {archiveRecord.original.status || 'pending'}
                    </span>
                  </td>
                  <td data-label="Actions" className="actions-cell">
                    <button
                      className="action-btn restore-btn"
                      onClick={() => handleRestoreBooking(archiveRecord)}
                      disabled={restoringArchiveId === archiveRecord.archiveId}
                    >
                      {restoringArchiveId === archiveRecord.archiveId ? 'Restoring...' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-results">
                  {viewMode === 'archived' ? 'No archived bookings found.' : 'No bookings found for this page.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {visiblePagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange((visiblePagination.currentPage || 1) - 1)}
            disabled={!visiblePagination.hasPrev}
          >
            Previous
          </button>
          <span>
            Page {visiblePagination.currentPage} of {visiblePagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange((visiblePagination.currentPage || 1) + 1)}
            disabled={!visiblePagination.hasNext}
          >
            Next
          </button>
        </div>
      )}

      {/* Render the Modal. It will only be visible if isModalOpen is true */}
      <EditBookingModal
        booking={selectedBooking}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveBooking}
      />
    </div>
  );
};

export default ManageBookings;
